import { useQuery, useMutation, useQueryClient, UseQueryOptions } from 'react-query'
import {
  CreateVisitRequest as BaseCreateVisitRequest,
  CreateVisitGuestRequest,
  ResponseError,
  ShowVisitResponse,
  UpdateVisitRequest as BaseUpdateVisitRequest,
  Visit,
  VisitsApi,
  VisitGuestResponse,
  SendVisitNotificationResponse,
  ListVisitsResponse,
} from '@trybeapp/sdk'
import { useCurrentSiteId } from 'contexts/SiteConfig'
import { apiConfiguration, formatDateForApi } from 'utilities/TrybeApiClient'
import { toIsoDate } from 'utilities/DateUtils/dateUtils'
import { OverrideProperties } from 'type-fest'

const api = new VisitsApi(apiConfiguration)

export const useVisits = (
  params: any = {},
  options: UseQueryOptions<ListVisitsResponse, ResponseError> = {}
) => {
  params.siteId = useCurrentSiteId()
  const formattedParams = formatDateParams(params)

  return useQuery<ListVisitsResponse, ResponseError>(
    ['visits', formattedParams],
    () => api.getVisits(formattedParams),
    options
  )
}

export const useVisitSmartFilters = () => {
  const siteId = useCurrentSiteId()

  return useQuery(['visitSmartFilters', { siteId }], () => api.getVisitsSmartFilters({ siteId }))
}

export const useVisit = (
  visitId: string,
  options: UseQueryOptions<ShowVisitResponse, ResponseError> = {}
) => {
  return useQuery<ShowVisitResponse, ResponseError>(
    ['visit', visitId],
    () =>
      api.getVisit({
        visitId,
      }),
    options
  )
}

type CreateVisitRequest = OverrideProperties<
  BaseCreateVisitRequest,
  {
    arrival_date: Date
    departure_date: Date
  }
>

export const useCreateVisit = () => {
  const siteId = useCurrentSiteId()

  return useMutation<ShowVisitResponse, ResponseError, CreateVisitRequest>((visit) => {
    return api.createNewVisit({
      siteId,
      createVisitRequest: {
        ...visit,
        arrival_date: toIsoDate(visit.arrival_date)!,
        departure_date: toIsoDate(visit.departure_date)!,
      },
    })
  })
}

type UpdateVisitRequest = OverrideProperties<
  BaseUpdateVisitRequest,
  {
    arrival_date: Date
    departure_date: Date
  }
>

export const useUpdateVisit = () => {
  const queryClient = useQueryClient()

  return useMutation<ShowVisitResponse, ResponseError, [Visit['id'], UpdateVisitRequest]>(
    ([visitId, visit]) => {
      return api.updateVisit({
        visitId,
        updateVisitRequest: {
          ...visit,
          arrival_date: toIsoDate(visit.arrival_date)!,
          departure_date: toIsoDate(visit.departure_date)!,
        },
      })
    },
    {
      onSuccess: (data, [visitId, visit]) => {
        // Invalidate the specific visit cache
        queryClient.invalidateQueries(['visit', visitId])
        queryClient.invalidateQueries(['visits'])
      },
    }
  )
}

export const useDeleteVisit = () => {
  const queryClient = useQueryClient()

  return useMutation(
    (visitId: string) =>
      api.deleteVisit({
        visitId,
      }),
    {
      onSuccess: (_, visitId) => {
        queryClient.invalidateQueries(['visit'])
        queryClient.invalidateQueries(['visit', visitId])
      },
    }
  )
}

export const useSendVisitNotification = () => {
  const queryClient = useQueryClient()

  return useMutation<SendVisitNotificationResponse, ResponseError, Visit['id']>(
    (visitId) => {
      return api.actionSendVisitNotification({
        visitId,
      })
    },
    {
      onSuccess: (_, visitId) => {
        queryClient.invalidateQueries(['visit'])
        queryClient.invalidateQueries(['visit', visitId])
      },
    }
  )
}

export const useCreateVisitGuest = () => {
  const queryClient = useQueryClient()

  return useMutation<VisitGuestResponse, ResponseError, [string, CreateVisitGuestRequest]>(
    ([visitId, visitGuest]) =>
      api.createVisitGuest({
        visitId,
        createVisitGuestRequest: visitGuest,
      }),
    {
      onSuccess: (_, visitId) => {
        queryClient.invalidateQueries(['visit'])
        queryClient.invalidateQueries(['visit', visitId])
      },
    }
  )
}

export const useDeleteVisitGuest = () => {
  const queryClient = useQueryClient()

  return useMutation<void, ResponseError, [string, string]>(
    ([visitId, guestId]) =>
      api.deleteVisitGuest({
        visitId,
        guestId,
      }),
    {
      onSuccess: (_, visitId) => {
        queryClient.invalidateQueries(['visit'])
        queryClient.invalidateQueries(['visit', visitId])
      },
    }
  )
}

const formatDateParams = (params: any) => {
  return Object.keys(params).reduce((formattedParams, key) => {
    if (['createdAtDateFrom', 'createdAtDateTo'].includes(key)) {
      formattedParams[key] = formatDateForApi(params[key])
    } else {
      formattedParams[key] = params[key]
    }
    return formattedParams
  }, {})
}
